/**
 * Hero images config — single source of truth for Gen AI hero assets.
 * All paths point to frontend/public/img/heroes and .../thumbnails.
 * See img/IMPLEMENTATION_STATUS.md for pipeline and status.
 */

const HEROES_BASE = '/img/heroes';
const THUMBS_BASE = '/img/thumbnails';

/** All hero image paths (slug → hero URL). Every file in public/img/heroes/ should be listed. */
export const heroImages = {
  // Tickets (client Gen AI batch)
  concerts: `${HEROES_BASE}/concerts-hero.webp`,
  sports: `${HEROES_BASE}/sports-hero.webp`,
  theater: `${HEROES_BASE}/theater-hero.webp`,
  soccer: `${HEROES_BASE}/soccer-hero.webp`,
  tennis: `${HEROES_BASE}/tennis-hero.webp`,
  golf: `${HEROES_BASE}/golf-hero.webp`,
  nba: `${HEROES_BASE}/nba-hero.webp`,
  baseball: `${HEROES_BASE}/baseball-hero.webp`,
} as const;

/** All thumbnail paths (slug → thumb URL). Every file in public/img/thumbnails/ should be listed. */
export const thumbImages = {
  concerts: `${THUMBS_BASE}/concerts-thumb.webp`,
  sports: `${THUMBS_BASE}/sports-thumb.webp`,
  theater: `${THUMBS_BASE}/theater-thumb.webp`,
  soccer: `${THUMBS_BASE}/soccer-thumb.webp`,
  tennis: `${THUMBS_BASE}/tennis-thumb.webp`,
  golf: `${THUMBS_BASE}/golf-thumb.webp`,
  nba: `${THUMBS_BASE}/nba-thumb.webp`,
  baseball: `${THUMBS_BASE}/baseball-thumb.webp`,
} as const;

/** Default hero when no category (e.g. home hero, events list). */
export const defaultHero = heroImages.concerts;

/** Category hero map for /events?category=… (key = category slug). Swap hero when filter changes. */
export const categoryHeroImages: Record<string, string> = {
  concerts: heroImages.concerts,
  sports: heroImages.sports,
  theater: heroImages.theater,
  nba: heroImages.nba,
  soccer: heroImages.soccer,
  tennis: heroImages.tennis,
  festivals: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1600&q=80',
};

/** Category thumbnail map for home page category cards and carousel. */
export const categoryThumbImages: Record<string, string> = {
  concerts: thumbImages.concerts,
  sports: thumbImages.sports,
  theater: thumbImages.theater,
  nba: thumbImages.nba,
  soccer: thumbImages.soccer,
  tennis: thumbImages.tennis,
  festivals: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600&q=80',
};

/** Fallback when an event has no imageUrl (e.g. event cards, grid). */
export const fallbackEventImage = thumbImages.concerts;

/** League → hero mapping for sports subcategories (when ?category=sports&league=X). */
export const leagueHeroImages: Record<string, string> = {
  nba: heroImages.nba,
  mlb: heroImages.baseball,
  soccer: heroImages.soccer,
  tennis: heroImages.tennis,
  golf: heroImages.golf,
  nfl: heroImages.sports,  // American football is the default "sports" hero
};
